import { useEffect, useState, useCallback } from 'react';
import type {
  ISlackConnection,
  ISlackMessageLogEntry,
  ISlackUser,
  SlackConnectionStatus,
} from '@skystone/shared';
import { slackApi } from '@/services/slack-api';
import { getErrorMessage } from '@/lib/utils';
import { Badge, PageLoading, ErrorBanner } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { SlackConnectionCard } from '@/components/slack/SlackConnectionCard';
import { SlackTestMessageCard } from '@/components/slack/SlackTestMessageCard';
import { SlackMessageLogCard } from '@/components/slack/SlackMessageLogCard';
import { SlackUserSelector } from '@/components/SlackUserSelector';

const STATUS_BADGE_MAP: Record<SlackConnectionStatus, { label: string; variant: 'success' | 'warning' | 'secondary' }> = {
  connected: { label: 'Connected', variant: 'success' },
  disconnected: { label: 'Disconnected', variant: 'warning' },
  not_configured: { label: 'Not Configured', variant: 'secondary' },
};

export function SlackSettingsTab() {
  const [connection, setConnection] = useState<ISlackConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action states
  const [verifying, setVerifying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Test message
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [testEmail, setTestEmail] = useState('');

  // Message log
  const [logEntries, setLogEntries] = useState<ISlackMessageLogEntry[]>([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const logPageSize = 10;

  // Find a member
  const [selectedUser, setSelectedUser] = useState<ISlackUser | null>(null);

  const loadConnection = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await slackApi.getConnectionStatus();
      setConnection(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load Slack connection status'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessageLog = useCallback(async (page: number): Promise<void> => {
    try {
      const response = await slackApi.getMessageLog({ page, pageSize: logPageSize });
      setLogEntries(response.data);
      setLogTotal(response.pagination.total);
      setLogPage(page);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load message log'));
    }
  }, []);

  useEffect(() => {
    loadConnection();
    loadMessageLog(1);
  }, [loadConnection, loadMessageLog]);

  const handleVerify = async (): Promise<void> => {
    setVerifying(true);
    setError(null);
    try {
      const response = await slackApi.verifyConnection();
      setConnection(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to verify Slack connection'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    setDisconnecting(true);
    setError(null);
    try {
      const response = await slackApi.disconnect();
      setConnection(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to disconnect Slack'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSendTest = async (): Promise<void> => {
    setSendingTest(true);
    setTestResult(null);
    setError(null);
    try {
      const email = testEmail.trim() || undefined;
      const response = await slackApi.sendTestMessage(email);
      setTestResult(response);
      loadMessageLog(1);
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to send test message');
      setTestResult({ success: false, error: message });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  const status = connection?.status ?? 'not_configured';
  const badge = STATUS_BADGE_MAP[status];
  const isConnected = status === 'connected';
  const totalLogPages = Math.ceil(logTotal / logPageSize);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Configure the Slack Bot integration for direct messages.
      </p>

      <ErrorBanner message={error} />

      <SlackConnectionCard
        connection={connection}
        status={status}
        badge={badge}
        isConnected={isConnected}
        verifying={verifying}
        disconnecting={disconnecting}
        onVerify={handleVerify}
        onDisconnect={handleDisconnect}
      />

      <SlackTestMessageCard
        isConnected={isConnected}
        sendingTest={sendingTest}
        testEmail={testEmail}
        testResult={testResult}
        onTestEmailChange={setTestEmail}
        onSendTest={handleSendTest}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Find a Member</CardTitle>
          <CardDescription>
            Search cached Slack workspace members. Members are synced daily via scheduled jobs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="slack-user-search" className="mb-1 block text-sm font-medium text-foreground">
                Search
              </label>
              <SlackUserSelector
                id="slack-user-search"
                onSelect={setSelectedUser}
                value={selectedUser}
                connectionStatus={status}
              />
            </div>

            {selectedUser && (
              <div className="flex items-center gap-3 rounded-md border border-border bg-muted/50 p-3">
                {selectedUser.avatarUrl ? (
                  <img
                    src={selectedUser.avatarUrl}
                    alt=""
                    className="h-10 w-10 rounded-full"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground">
                    {(selectedUser.displayName || selectedUser.realName || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">
                    {selectedUser.displayName || selectedUser.realName}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {selectedUser.email ?? selectedUser.slackId}
                  </p>
                </div>
                <Badge variant="secondary">{selectedUser.slackId}</Badge>
              </div>
            )}

            {!isConnected && (
              <p className="text-xs text-muted-foreground">
                Connect to a Slack workspace first to browse members.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <SlackMessageLogCard
        logEntries={logEntries}
        logPage={logPage}
        totalLogPages={totalLogPages}
        logTotal={logTotal}
        onPageChange={(page) => loadMessageLog(page)}
      />
    </div>
  );
}
